import React, { FC, useRef, useEffect } from 'react';
import { graphql } from 'gatsby';
import lottie from "lottie-web";

import { Heading, Animation } from './Hero.styles'
import { Box, Button, InverseCorner, Container, ButtonProps, FileAsset } from '@mode/components';
import { HeroImage } from './HeroImage';
import { MobileTitle } from './MobileTitle';

interface HeroProps {
  title: string;
  description: string;
  image?: unknown;
  buttons?: ButtonProps[];
  animation: FileAsset;
}

export const Hero: FC<HeroProps> = ({ title, description, image, buttons, animation }) => {
  const splitTitle = title.split(' ');
  const animationRef = useRef(null);
  const animationRefDesktop = useRef(null);
  useEffect(() => {
    lottie.loadAnimation({
      container: animationRef.current,
      path: animation.url,
      loop: false,
      renderer: "svg",
      autoplay: true
    })
    lottie.loadAnimation({
      container: animationRefDesktop.current,
      path: animation.url,
      loop: false,
      renderer: "svg",
      autoplay: true
    })
  }, [animation.url])

  return (
    <Container css={{ paddingTop: '$xxs', '@md': { paddingTop: '$sm' } }}>
      <Box
        css={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          '@md': {
            display: 'block'
          }
        }}>
        <Box css={{ display: 'none', order: 2, flexDirection: 'column', gridColumn: 'auto / span 10', '@md': { display: 'inline-flex' } }}>
          <Heading css={{
            borderBottomRightRadius: 0
          }}>
            {splitTitle[0]} {splitTitle[1]}
          </Heading>
          <Box css={{ alignSelf: 'flex-end' }}>
            <Heading
              css={{
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0
              }}
            >
              {splitTitle[2]} {splitTitle[3]}
              <InverseCorner position="topLeft" />
              <InverseCorner position="bottomRight" />
            </Heading>
          </Box>
        </Box>
        <Box
          css={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gridRow: '2 / span 1',
            gridColumn: '7 / span 6',
            width: '100%',
            order: 2,
            '@md': {
              alignItems: 'flex-end',
            }
          }}
        >
          <MobileTitle title={title} />
          <Heading css={{
            maxWidth: '550px',
            borderTopLeftRadius: 0,
            display: 'none',
            '@md': {
              display: 'block'
            }
          }}>
            {splitTitle[4]} {splitTitle[5]}
            <InverseCorner position="topLeft" />
          </Heading>
          <Box css={{
            width: '100%',
            '@md': {
              width: '38vw',
            },
            '@lg': {
              width: '36vw',
            },
            '@xl': {
              width: '490px'
            }
          }}>
            <Box
              as="p"
              css={{
                color: '$g1',
                paddingTop: '32px',
                paddingBottom: '32px',
                fontSize: '$p1',
                lineHeight: '$130',
                fontFamily: '$sans',
                fontWeight: 400
              }}
            >
              {description}
            </Box>
            {buttons && buttons.map((button, index) => {
              return (
                <Button
                  as={button.calendly ? "button" : "link"}
                  key={button._key}
                  variant={button.type}
                  to={button.url}
                  css={{ ml: index === 0 ? 0 : '$xxxs' }}
                  dark
                  calendly={button.calendly}
                  calendlyUrl={button.calendlyUrl}
                  marketo={button.marketo}
                  marketoForm={button.marketoForm}
                  pardot={button.pardot}
                  pardotForm={button?.pardotForm?.config || button?.pardotForm}
                  hidden={button.hidden}
                >
                  {button.label}
                </Button>
              )
            })}
          </Box>
        </Box>
        <Box as="span" css={{ lineHeight: 0, height: 0, display: 'none', '@md': { display: 'block' } }}>
          <Animation ref={animationRefDesktop} />
        </Box>
        <HeroImage image={image}>
          <Box css={{ display: 'block', '@md': { display: 'none' } }}>
            <Animation ref={animationRef} />
          </Box>
        </HeroImage>
      </Box>
    </Container >
  )
}

export const query = graphql`
  fragment HomeHeroContent on HomeHero {
    _key
    title
    description
    buttons {
      ...ButtonContent
    }
    image {
      ...ImageWithPreview
    }
    animation {
      _createdAt
      size
      url
    }
  }
`
